import { motion, AnimatePresence } from 'motion/react';
import { useEffect, useState } from 'react';
import { FolderOpen } from 'lucide-react';
import { Icon, cn } from '@ideoz/ui';

const stages = [
  { id: 1, text: 'Collecting project context' },
  { id: 2, text: 'Deriving design insights' },
  { id: 3, text: 'Exploring layout options' },
  { id: 4, text: 'Assembling prototype' },
];

const STAGE_DURATION = 3000; // 3 seconds per stage
const END_PAUSE_DURATION = 2000; // 2 seconds pause before looping

export function PrototyperLoadingAnimation(): JSX.Element {
  const [currentStage, setCurrentStage] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(
      () => {
        setCurrentStage((prev) => {
          const nextStage = (prev + 1) % stages.length;
          return nextStage;
        });
      },
      currentStage === stages.length - 1 ? STAGE_DURATION + END_PAUSE_DURATION : STAGE_DURATION
    );

    return () => clearTimeout(timeout);
  }, [currentStage]);

  return (
    <div
      className={cn(
        `w-[300px] flex flex-col items-center justify-center gap-4 rounded-2xl p-4`,
        currentStage === 3 ? 'h-[260px]' : 'h-[240px]'
      )}
    >
      <div className="relative w-full h-full flex items-center justify-center">
        <AnimatePresence mode="wait">
          {currentStage === 0 && <Stage1 key="stage1" />}
          {currentStage === 1 && <Stage2 key="stage2" />}
          {currentStage === 2 && <Stage3 key="stage3" />}
          {currentStage === 3 && <Stage4 key="stage4" />}
        </AnimatePresence>
      </div>
      <motion.p
        key={currentStage}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="text-primary-800 text-sm"
      >
        {/* {stages[currentStage].text} */}
      </motion.p>
    </div>
  );
}

/**
 * Compact, indefinitely-looping variant of {@link Stage1}'s "gathering" motif — for loading
 * moments that aren't the AI actively generating (installing deps, building, reconnecting to a
 * saved snapshot…). Those states shouldn't borrow the 4-stage animation's specific narrative
 * ("deriving design insights" mid-build makes no sense), but a bare spinner icon feels
 * disconnected from the rest of the prototyper UI — this gives loading moments everywhere in
 * the panel the same visual language without claiming anything untrue about what's happening.
 */
export function PrototyperLoadingIcon({ size = 56 }: { size?: number }): JSX.Element {
  const items = [
    { x: -1, y: -0.75, delay: 0 },
    { x: 1, y: -0.6, delay: 0.3 },
    { x: -0.8, y: 0.6, delay: 0.6 },
    { x: 0.9, y: 0.8, delay: 0.9 },
  ];

  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size }}
      role="status"
      aria-label="Loading"
    >
      <motion.div
        initial={{ scale: 0.85, opacity: 0.6 }}
        animate={{ scale: [0.85, 1, 0.85], opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 2, repeat: Infinity, ease: [0.4, 0, 0.2, 1] }}
        className="absolute inset-0 flex items-center justify-center"
      >
        <div
          className="rounded-full border-2 border-primary-200"
          style={{ width: size * 0.5, height: size * 0.5 }}
        />
      </motion.div>
      {items.map((item, index) => (
        <motion.div
          key={index}
          initial={{ x: item.x * size * 0.4, y: item.y * size * 0.4, opacity: 0, scale: 1 }}
          animate={{
            x: [item.x * size * 0.4, 0, item.x * size * 0.4],
            y: [item.y * size * 0.4, 0, item.y * size * 0.4],
            opacity: [0, 1, 0],
            scale: [1, 0.85, 1],
          }}
          transition={{
            delay: item.delay,
            duration: 2.4,
            repeat: Infinity,
            ease: [0.4, 0, 0.2, 1],
          }}
          className="absolute inset-0 flex items-center justify-center"
        >
          <Icon icon={FolderOpen} size={size * 0.2} className="stroke-primary-400" />
        </motion.div>
      ))}
    </div>
  );
}

// Stage 1: Documents and tools flowing into center
function Stage1() {
  const items = [
    { x: -100, y: -80, delay: 0 },
    { x: 100, y: -60, delay: 0.2 },
    { x: -80, y: 60, delay: 0.4 },
    { x: 90, y: 80, delay: 0.6 },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 flex items-center justify-center"
    >
      {/* Center target */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        className="w-20 h-20 rounded-full border-2 border-primary-200"
      />

      {/* Flowing items */}
      {items.map((item, index) => (
        <motion.div
          key={index}
          initial={{ x: item.x, y: item.y, opacity: 0, scale: 1 }}
          animate={{
            x: 0,
            y: 0,
            opacity: [0, 1, 0.4],
            scale: [1, 1, 0.9],
          }}
          transition={{
            delay: item.delay,
            duration: 1.5,
            ease: [0.4, 0, 0.2, 1],
          }}
          className="absolute"
        >
          <Icon icon={FolderOpen} size={24} className="stroke-primary-300" />
        </motion.div>
      ))}
    </motion.div>
  );
}

// Stage 2: Context compresses into insight labels
function Stage2() {
  const insights = [
    'User needs',
    'Patterns',
    'Constraints',
    'Design principles',
    'Brand guidelines',
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 flex items-center justify-center overflow-hidden before:content-[''] before:absolute before:top-[-10px] before:left-0 before:z-10 before:w-full before:h-[40px] before:bg-gradient-to-b before:from-gray-100 before:to-transparent"
    >
      {/* Initial blob that compresses */}
      <motion.div
        initial={{ scale: 1.5, opacity: 0.2 }}
        animate={{ scale: 0, opacity: 0 }}
        transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
        className="absolute w-32 h-32 rounded-full bg-primary-100"
      />

      {/* Insight labels appearing */}
      <motion.div
        initial={{ opacity: 1, y: 0 }}
        animate={{ opacity: 1, y: -40 }}
        // exit={{ opacity: 0, y: 0 }}
        transition={{
          delay: 1,
          duration: 2,
          ease: [0.4, 0, 0.2, 1],
        }}
        className="flex flex-col gap-3"
      >
        {insights.map((insight, index) => (
          <motion.div
            key={insight}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              delay: 0.4 + index * 0.15,
              duration: 0.4,
              ease: [0.4, 0, 0.2, 1],
            }}
            className="px-5 py-2 bg-blue-light-50/50 border border-blue-200 rounded-full text-xs text-center text-primary-600"
          >
            {insight}
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
}

// Stage 3: Wireframe options appear, one is selected
function Stage3() {
  const [selected, setSelected] = useState<number | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setSelected(1), 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 flex items-center justify-center gap-6"
    >
      {[0, 1, 2].map((index) => (
        <motion.div
          key={index}
          initial={{ scale: 0, opacity: 0 }}
          animate={{
            scale: selected === index ? 1.1 : 1,
            opacity: selected === null ? 1 : selected === index ? 1 : 0.7,
          }}
          transition={{
            delay: index * 0.15,
            duration: 0.4,
            ease: [0.4, 0, 0.2, 1],
          }}
          className="relative"
        >
          <WireframeOption selected={selected === index} />
        </motion.div>
      ))}
    </motion.div>
  );
}

// Stage 4: Components snap together into a screen
function Stage4() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 flex items-center justify-center"
    >
      {/* Device frame */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
        className="w-72 h-48 rounded-2xl border border-primary-200 relative overflow-hidden bg-white"
      >
        {/* Header bar */}
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
          className="absolute top-6 left-[20px] right-[20px] h-[40px] bg-primary-100 rounded border-2 border-dashed border-primary-300"
        />

        {/* Content area */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
          className="absolute top-[80px] left-[20px] w-[30px] bottom-6 bg-secondary-50 rounded border-2 border-dashed border-secondary-300"
        />
        <motion.div
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
          className="absolute top-[80px] left-[60px] right-6 bottom-6 bg-blue-light-50 rounded border-2 border-dashed border-blue-light-300"
        />
      </motion.div>
    </motion.div>
  );
}

// Wireframe option component
function WireframeOption({ selected }: { selected: boolean }) {
  return (
    <div
      className={`w-16 h-20 rounded border-1 transition-colors ${
        selected ? 'border-primary-400/' : 'border-primary-200'
      } bg-white p-2 flex flex-col gap-1`}
    >
      <div className={`h-2 rounded ${selected ? 'bg-primary-400' : 'bg-primary-100'}`} />
      <div className={`h-1 rounded ${selected ? 'bg-primary-300' : 'bg-primary-50'}`} />
      <div className={`h-1 rounded ${selected ? 'bg-primary-300' : 'bg-primary-50'}`} />
      <div className="flex-1" />
      <div className={`h-1.5 rounded ${selected ? 'bg-blue-light-300' : 'bg-primary-50'}`} />
    </div>
  );
}
